import { useContext, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import {
  IconBallAmericanFootball,
  IconBuildingStore,
  IconChartBar,
  IconTrophy,
  IconCards,
  IconCirclePlus,
  IconWallet,
  IconBell,
  IconGift,
  IconUserCircle,
  IconLifebuoy,
  IconLogout,
  IconLogin,
  IconPlus,
} from '@tabler/icons-react';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { useIsMenuBreakpoint } from '~/domains/common/hooks/mediaQueries';
import IconHome from '~/domains/design-system/icons/IconHome';
import IconCrown from '~/domains/design-system/icons/IconCrown';
import { Routes } from '~/domains/common/constants/routes';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { REFER_AMOUNT } from '~/domains/user/domains/refer-a-friend/consts';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { SocureKycStatus } from '~/services/users/types';
import { isPathMatch } from '~/utils/isPathMatch';
import dt from '~/testing';

import type { UserMenuProps } from './types';

function useUserMenu({
  user,
  KYCStatus,
  notificationsCount,
  promotionsCount,
}: Omit<UserMenuProps, 'onClose'>) {
  const { t } = useTranslation('common');
  const { openModalUnique } = useContext(RootModalsContext);
  const isMenuBreakpoint = useIsMenuBreakpoint();
  const router = useRouter();
  const isReferAFriendEnabled = useIsFeatureEnabled(FEATURE.ENABLE_REFER_A_FRIEND_V2);
  const isPredictionMarketsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_PREDICTION_MARKETS);
  const currentPath = router.asPath;

  const MenuItems = useMemo(
    () => ({
      divider: 'divider',
      home: {
        href: Routes.home(),
        text: t('appShellHeader3.userMenu.home'),
        Icon: <IconHome strokeWidth={1.5} />,
        isActive: isPathMatch(currentPath, Routes.home()),
        dataTestId: dt.common.components.appShellHeader.homeButton,
      },
      contestLobby: {
        href: Routes.contestLobby(),
        text: t('appShellHeader3.userMenu.contestLobby'),
        Icon: <IconBallAmericanFootball strokeWidth={1.5} />,
        isActive: isPathMatch(currentPath, Routes.contestLobby()),
      },
      quickPicks: {
        href: Routes.quickPicksBoard(),
        text: t('appShellHeader3.userMenu.quickPicks'),
        Icon: <IconCards />,
        isActive: isPathMatch(currentPath, Routes.quickPicksBoard(), false, ['/quick-picks']),
      },
      markets: {
        href: Routes.markets(),
        text: t('appShellHeader3.userMenu.markets'),
        Icon: <IconChartBar strokeWidth={1.5} />,
        isActive: isPathMatch(currentPath, Routes.markets(), false, ['/markets']),
      },
      entryMarketplace: {
        href: Routes.marketplace(),
        text: t('appShellHeader3.userMenu.entryMarketplace'),
        Icon: <IconBuildingStore strokeWidth={1.5} />,
        isActive: isPathMatch(currentPath, Routes.marketplace(), false, ['/marketplace']),
      },
      createAContest: {
        href: Routes.createAContest(),
        text: t('appShellHeader3.userWidget.cacLabel'),
        Icon: <IconPlus />,
        isActive: isPathMatch(currentPath, Routes.createAContest()),
      },
      myContests: {
        href: Routes.entries(),
        text: t('appShellHeader3.userMenu.myContests'),
        Icon: <IconTrophy />,
        isActive: isPathMatch(currentPath, Routes.entries()),
      },
      commissioner: {
        href: Routes.commissioner(),
        text: t('appShellHeader3.userMenu.commissioner'),
        Icon: <IconCrown />,
        isActive: isPathMatch(currentPath, Routes.commissioner()),
      },
      deposit: {
        href: Routes.deposit(),
        text: t('appShellHeader3.userMenu.deposit'),
        Icon: <IconCirclePlus />,
        isActive: isPathMatch(currentPath, Routes.deposit()),
        dataTestId: dt.common.components.appShellHeader.depositButton,
      },
      withdraw: {
        href: Routes.withdraw(),
        text: t('appShellHeader3.userMenu.withdraw'),
        Icon: <IconWallet />,
        isActive: isPathMatch(currentPath, Routes.withdraw()),
      },
      notifications: {
        href: Routes.notifications(),
        text: t('appShellHeader3.userMenu.notifications'),
        Icon: <IconBell />,
        count: notificationsCount > 99 ? '99+' : notificationsCount,
        isActive: isPathMatch(currentPath, Routes.notifications()),
        dataTestId: dt.common.components.appShellHeader.notificationsButton,
      },
      promotions: {
        href: Routes.promotions(),
        text: t('appShellHeader3.userMenu.promotions'),
        Icon: <IconGift size={24} />,
        ...(promotionsCount && { count: promotionsCount > 99 ? '99+' : promotionsCount }),
        isActive: isPathMatch(currentPath, Routes.promotions()),
      },
      inviteFriends: {
        text: t('appShellHeader3.userMenu.inviteFriends', { amount: REFER_AMOUNT }),
        Icon: <IconGift />,
        onClick: () => {
          openModalUnique({ type: 'RAFModal' });
        },
        dataTestId: dt.common.components.banner.RAFbanner,
      },
      myAccount: {
        href: Routes.account(),
        text: t('appShellHeader3.userMenu.myAccount'),
        Icon: <IconUserCircle />,
        isActive: isPathMatch(currentPath, Routes.account()),
      },
      helpCenter: {
        href: Routes.helpCenter(),
        text: t('appShellHeader3.userMenu.helpCenter'),
        Icon: <IconLifebuoy />,
      },
      signOut: {
        href: Routes.logout(),
        text: t('appShellHeader3.userMenu.signOut'),
        Icon: <IconLogout />,
        isCritical: true,
      },
      logIn: {
        href: Routes.signIn(),
        text: t('appShellHeader3.userMenu.logIn'),
        Icon: <IconLogin />,
      },
    }),
    [t, user, notificationsCount, promotionsCount, currentPath, openModalUnique]
  );

  const menuConfig = useMemo(() => {
    if (!user)
      return [
        ...(isMenuBreakpoint
          ? [
              MenuItems.home,
              MenuItems.contestLobby,
              MenuItems.quickPicks,
              ...(isPredictionMarketsEnabled ? [MenuItems.markets] : []),
              MenuItems.entryMarketplace,
              MenuItems.createAContest,
            ]
          : []),
        MenuItems.helpCenter,
        MenuItems.logIn,
      ];

    return [
      ...(KYCStatus !== SocureKycStatus.ACCEPT ? [MenuItems.divider] : []),
      ...(isMenuBreakpoint
        ? [
            MenuItems.home,
            MenuItems.contestLobby,
            MenuItems.myContests,
            MenuItems.quickPicks,
            ...(isPredictionMarketsEnabled ? [MenuItems.markets] : []),
            MenuItems.entryMarketplace,
            MenuItems.commissioner,
            MenuItems.divider,
          ]
        : []),
      ...(KYCStatus !== SocureKycStatus.ACCEPT
        ? [MenuItems.deposit, MenuItems.withdraw, MenuItems.divider]
        : []),
      MenuItems.notifications,
      MenuItems.promotions,
      ...(isReferAFriendEnabled ? [MenuItems.inviteFriends] : []),
      MenuItems.myAccount,
      MenuItems.helpCenter,
      MenuItems.signOut,
    ];
  }, [
    user,
    KYCStatus,
    isMenuBreakpoint,
    isReferAFriendEnabled,
    isPredictionMarketsEnabled,
    MenuItems,
  ]);

  return menuConfig;
}

export default useUserMenu;
